import { type FC, useCallback } from "react";

import { cn } from "@btnet/utilities-core";

import { recalculateAllFields } from "utilities/lineItem/markupUtility";

import { BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { Percentage } from "commonComponents/financial/Percentage/Percentage";
import { type IGridCellRenderProps } from "commonComponents/utilities/Grid/Grid.types";
import {
    type IPreviewLineItemProps,
    LineItemPreview,
} from "commonComponents/utilities/LineItemContainer/LineItemPreview";
import { GridLineItemColumnType } from "commonComponents/utilities/LineItemContainer/types/LineItem.enums";
import { type ILineItemColumn } from "commonComponents/utilities/LineItemContainer/types/LineItem.interfaces";
import {
    type IBaseLineItem,
    type IMarkupLineItem,
    LineItemColumns,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";
import { ValueDisplay } from "commonComponents/utilities/ValueDisplay/ValueDisplay";

import { MarkupColumnTypes } from "handlers";

export interface IMarginCell extends IMarkupLineItem, IPreviewLineItemProps {}

export const MarginCell: FC<IGridCellRenderProps<IMarginCell>> = (props) => {
    const { cellValue, onChange, setRowTouched, rowIndex, field } = props;

    const handleChange = useCallback(
        (_: string, margin?: number) => {
            const lineItem = cellValue;
            // Having a margin of 100% or more isn't possible unless the builder cost is 0.
            if (lineItem && (margin === undefined || margin < 100)) {
                const calculatedFields = recalculateAllFields({
                    ...lineItem,
                    markupType: MarkupColumnTypes.Margin,
                    margin: margin ?? 0,
                });
                onChange(field.path, {
                    ...lineItem,
                    ...calculatedFields,
                    margin: margin as number,
                });
            }
        },
        [cellValue, onChange, field.path],
    );

    const handleBlur = useCallback(() => {
        // need to set the client price and markup fields as touched as well
        setRowTouched!(rowIndex!, [field.name, "ownerPrice"], true);
        setRowTouched!(rowIndex!, [field.name, "markup"], true);

        const lineItem = cellValue;
        if (lineItem && lineItem.margin === undefined) {
            const calculatedFields = recalculateAllFields({
                ...lineItem,
                markupType: MarkupColumnTypes.Margin,
                margin: 0,
            });
            onChange(field.path, { ...lineItem, ...calculatedFields });
        }
    }, [setRowTouched, rowIndex, field.name, field.path, cellValue, onChange]);

    const renderMarginCell = () => {
        const { cellValue, field, isInEdit, canEdit, autoFocus, onFocus, isDisabled } = props;

        if (!cellValue) {
            return <ValueDisplay value={undefined} />;
        }

        const marginFieldName = `${field.path}.margin`;
        const readOnly = !isInEdit || !canEdit;

        const component = (
            <LineItemPreview
                fieldName="margin"
                addPreviewListener={cellValue.addPreviewListener}
                render={(preview) => (
                    <Percentage
                        key={preview?.margin ?? "margin"}
                        id={marginFieldName}
                        disabled={
                            (preview?.markupType ?? cellValue.markupType) ===
                                MarkupColumnTypes.OwnerPrice || isDisabled
                        }
                        max={99.99}
                        data-testid={marginFieldName}
                        value={preview?.margin ?? cellValue.margin ?? undefined}
                        readOnly={readOnly}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        decimalScale={2}
                        onFocus={onFocus}
                        autoFocus={autoFocus}
                        className={preview ? "LineItemPreview" : undefined}
                        allowNegative
                        defaultToZero={false}
                    />
                )}
            />
        );

        if (isInEdit) {
            return (
                <BTFormItemAutomatic
                    id={marginFieldName}
                    className={cn(
                        "LineItemCell",
                        {
                            RightAlignFormLabel: !canEdit,
                        },
                        { ellipsis: cellValue?.ellipsis },
                    )}
                >
                    {component}
                </BTFormItemAutomatic>
            );
        }

        return (
            <ValueDisplay
                className={cn(
                    "LineItemCell",
                    { lineItemEditableCell: canEdit },
                    { ellipsis: cellValue?.ellipsis },
                )}
                value={component}
                tabIndex={canEdit ? 0 : undefined}
                onFocus={onFocus}
            />
        );
    };

    return <> {renderMarginCell()} </>;
};

export const marginCellDefaultConfig = <T extends IBaseLineItem>() =>
    ({
        id: LineItemColumns.Margin,
        name: "Margin",
        jsonKey: "margin",
        width: 100.0,
        minWidth: 48,
        ellipsis: true,
        align: "right",
        isSortable: false,
        columnType: GridLineItemColumnType.margin,
        isFrozen: false,
        isHidden: false,
        Cell: MarginCell,
    }) as Partial<ILineItemColumn<T, IMarkupLineItem>>;
